import { useState, useEffect, useMemo } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { 
  Plus, 
  Edit2, 
  Trash2,
  TrendingUp,
  Target,
  Users,
  Trophy,
  Medal,
  Award,
  User
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { CurrencyInput } from "../components/ui/currency-input";
import { Slider } from "../components/ui/slider";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STAGES = {
  prospect: { label: "Prospect", color: "bg-blue-500" },
  negotiation: { label: "Negotiation", color: "bg-yellow-500" },
  verbal: { label: "Verbal Commit", color: "bg-orange-500" },
  contract: { label: "Contract", color: "bg-green-500" }
};

const MARKER_COLORS = {
  prospect: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-700", marker: "#3D6355" },
  negotiation: { bg: "bg-yellow-50", border: "border-yellow-300", text: "text-yellow-700", marker: "#8A8172" },
  verbal: { bg: "bg-orange-50", border: "border-orange-300", text: "text-orange-700", marker: "#A55A34" },
  contract: { bg: "bg-green-50", border: "border-green-300", text: "text-green-700", marker: "#2E7D55" }
};

const RANK_ICONS = [Trophy, Medal, Award];
const RANK_COLORS = ["text-yellow-500", "text-gray-400", "text-amber-600"];

function StatCard(props) {
  return (
    <Card className="border border-border">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {props.icon}
          <span className="text-xs">{props.label}</span>
        </div>
        <p className={"text-xl md:text-2xl font-bold " + (props.colorClass || "")} style={{ fontFamily: 'var(--font-sans)' }}>
          {props.value}
        </p>
      </CardContent>
    </Card>
  );
}

function formatCompactCurrency(value) {
  if (value >= 1000000) return "$" + (value / 1000000).toFixed(1) + "M";
  if (value >= 1000) return "$" + (value / 1000).toFixed(0) + "K";
  return "$" + value;
}

function WhiteboardCard(props) {
  var deal = props.deal;
  var colors = MARKER_COLORS[deal.stage] || MARKER_COLORS.prospect;
  var rotation = ((props.index % 5) - 2) * 2;
  
  var style = {
    left: "calc(" + props.xPercent + "% - 70px)",
    top: "calc(" + props.yPercent + "% - 40px)",
    transform: "rotate(" + rotation + "deg)"
  };

  return (
    <div
      className={"absolute cursor-pointer transition-all hover:scale-105 hover:z-30 z-20 w-[160px] " + colors.bg + " " + colors.border + " border-2 rounded shadow-md p-2"}
      style={style}
      onClick={props.onClick}
      data-testid={"deal-card-" + deal.id}
    >
      <div className="text-sm font-bold truncate" style={{ fontFamily: 'var(--font-sans)', fontSize: '18px', color: colors.marker }}>
        {deal.company_name}
      </div>
      <div className="text-lg font-bold" style={{ fontFamily: 'var(--font-sans)', fontSize: '22px' }}>
        {formatCompactCurrency(deal.value)}
      </div>
      <div className="text-xs mt-1 opacity-70" style={{ fontFamily: 'var(--font-sans)', fontSize: '14px' }}>
        {deal.daysToClose}d • {deal.confidence}%
      </div>
      {/* Deal Owner */}
      {deal.deal_owner_name && (
        <div className="text-[10px] mt-1 flex items-center gap-1 opacity-80">
          <User className="w-2.5 h-2.5" />
          <span className="truncate">{deal.deal_owner_name}</span>
        </div>
      )}
    </div>
  );
}

function StageLegend() {
  const items = [];
  for (const key in STAGES) {
    items.push(
      <div key={key} className="flex items-center gap-2">
        <div className={"w-3 h-3 rounded-full " + STAGES[key].color} />
        <span className="text-xs text-muted-foreground">{STAGES[key].label}</span>
      </div>
    );
  }
  return <div className="flex flex-wrap gap-3 mb-4">{items}</div>;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
}

const WhiteboardView = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [users, setUsers] = useState([]);
  const [newDeal, setNewDeal] = useState({
    deal_name: "",
    company_name: "",
    value: 0,
    estimated_close_date: "",
    description: "",
    stage: "prospect",
    confidence: 50,
    sales_rep: "",
    deal_owner_id: "",
    submitted_by_id: ""
  });

  useEffect(function() {
    fetchDeals();
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      const res = await apiClient.get("/users");
      // Filter to sales/COO/Admin users who can own deals
      const salesUsers = (res.data || []).filter(u => 
        ["COO", "Admin", "Sales", "Program Manager"].includes(u.role)
      );
      setUsers(salesUsers);
    } catch (e) {
      console.error("Failed to load users");
    }
  }

  async function fetchDeals() {
    try {
      const response = await apiClient.get("/deals");
      setDeals(response.data);
    } catch (error) {
      toast.error("Failed to load deals");
    } finally {
      setLoading(false);
    }
  }

  async function handleAddDeal() {
    if (!newDeal.deal_name.trim() || !newDeal.company_name.trim() || !newDeal.value || !newDeal.estimated_close_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const response = await apiClient.post("/deals", newDeal);
      setDeals(function(prev) { return [response.data].concat(prev); });
      setShowAddDeal(false);
      resetForm();
      toast.success("Deal added to the board!");
    } catch (error) {
      toast.error("Failed to add deal");
    }
  }

  async function handleUpdateDeal() {
    if (!editingDeal) return;
    try {
      const response = await apiClient.patch("/deals/" + editingDeal.id, newDeal);
      setDeals(function(prev) { return prev.map(function(d) { return d.id === editingDeal.id ? response.data : d; }); });
      setEditingDeal(null);
      resetForm();
      toast.success("Deal updated!");
    } catch (error) {
      toast.error("Failed to update deal");
    }
  }

  async function handleDeleteDeal(dealId) {
    try {
      await apiClient.delete("/deals/" + dealId);
      setDeals(function(prev) { return prev.filter(function(d) { return d.id !== dealId; }); });
      setSelectedDeal(null);
      toast.success("Deal removed");
    } catch (error) {
      toast.error("Failed to delete deal");
    }
  }

  function resetForm() {
    setNewDeal({
      deal_name: "",
      company_name: "",
      value: 0,
      estimated_close_date: "",
      description: "",
      stage: "prospect",
      confidence: 50,
      sales_rep: ""
    });
  }

  function startEdit(deal) {
    setNewDeal({
      deal_name: deal.deal_name,
      company_name: deal.company_name,
      value: deal.value,
      estimated_close_date: deal.estimated_close_date,
      description: deal.description || "",
      stage: deal.stage,
      confidence: deal.confidence,
      sales_rep: deal.sales_rep || ""
    });
    setEditingDeal(deal);
  }

  const chartData = useMemo(function() {
    if (deals.length === 0) return { maxValue: 500000, maxDays: 90, positions: [] };
    
    const today = new Date();
    const maxValue = Math.max.apply(null, deals.map(function(d) { return d.value; }).concat([100000])) * 1.2;
    const maxDays = 120;
    
    const positions = [];
    for (let i = 0; i < deals.length; i++) {
      const deal = deals[i];
      const closeDate = new Date(deal.estimated_close_date);
      const daysToClose = Math.max(0, Math.ceil((closeDate - today) / (1000 * 60 * 60 * 24)));
      const xPercent = Math.min((daysToClose / maxDays) * 100, 100);
      const yPercent = 100 - Math.min((deal.value / maxValue) * 100, 100);
      const size = Math.max(40, Math.min(80, 40 + (deal.value / maxValue) * 40));
      
      positions.push({
        id: deal.id,
        deal_name: deal.deal_name,
        company_name: deal.company_name,
        value: deal.value,
        estimated_close_date: deal.estimated_close_date,
        description: deal.description,
        stage: deal.stage,
        confidence: deal.confidence,
        sales_rep: deal.sales_rep,
        created_by_name: deal.created_by_name,
        created_at: deal.created_at,
        xPercent: xPercent,
        yPercent: yPercent,
        daysToClose: daysToClose,
        size: size
      });
    }
    
    return { maxValue: maxValue, maxDays: maxDays, positions: positions };
  }, [deals]);

  // Calculate leaderboard data
  const leaderboard = useMemo(function() {
    const repStats = {};
    
    for (let i = 0; i < deals.length; i++) {
      const deal = deals[i];
      const rep = deal.sales_rep || "Unassigned";
      
      if (!repStats[rep]) {
        repStats[rep] = {
          name: rep,
          totalValue: 0,
          weightedValue: 0,
          dealCount: 0,
          avgConfidence: 0,
          stages: { prospect: 0, negotiation: 0, verbal: 0, contract: 0 }
        };
      }
      
      repStats[rep].totalValue += deal.value;
      repStats[rep].weightedValue += deal.value * deal.confidence / 100;
      repStats[rep].dealCount += 1;
      repStats[rep].stages[deal.stage] = (repStats[rep].stages[deal.stage] || 0) + 1;
    }
    
    // Calculate averages and sort
    const reps = [];
    for (const rep in repStats) {
      const stats = repStats[rep];
      stats.avgConfidence = Math.round(stats.totalValue > 0 ? (stats.weightedValue / stats.totalValue) * 100 : 0);
      reps.push(stats);
    }
    
    // Sort by weighted value (descending)
    reps.sort(function(a, b) { return b.weightedValue - a.weightedValue; });
    
    return reps;
  }, [deals]);

  const totalPipeline = deals.reduce(function(sum, d) { return sum + d.value; }, 0);
  const avgConfidence = deals.length > 0 
    ? Math.round(deals.reduce(function(sum, d) { return sum + d.confidence; }, 0) / deals.length) 
    : 0;
  const weightedPipeline = deals.reduce(function(sum, d) { return sum + (d.value * d.confidence / 100); }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  function renderDealCards() {
    const cards = [];
    for (let i = 0; i < chartData.positions.length; i++) {
      const deal = chartData.positions[i];
      cards.push(
        <WhiteboardCard
          key={deal.id}
          deal={deal}
          index={i}
          xPercent={deal.xPercent}
          yPercent={deal.yPercent}
          onClick={function() { setSelectedDeal(deal); }}
        />
      );
    }
    return cards;
  }

  function renderYLabels() {
    const labels = [];
    const values = [1, 0.75, 0.5, 0.25, 0];
    for (let i = 0; i < values.length; i++) {
      labels.push(
        <span key={i} style={{ fontFamily: 'var(--font-sans)' }}>{formatCurrency(chartData.maxValue * values[i])}</span>
      );
    }
    return labels;
  }

  function renderGridLines() {
    const lines = [];
    const percents = [0, 25, 50, 75, 100];
    for (let i = 0; i < percents.length; i++) {
      lines.push(
        <div key={"h-" + i} className="absolute w-full border-t border-gray-300/50" style={{ top: percents[i] + "%" }} />
      );
      lines.push(
        <div key={"v-" + i} className="absolute h-full border-l border-gray-300/50" style={{ left: percents[i] + "%" }} />
      );
    }
    return lines;
  }

  function renderStageOptions() {
    const options = [];
    for (const key in STAGES) {
      options.push(
        <SelectItem key={key} value={key}>
          <div className="flex items-center gap-2">
            <div className={"w-2 h-2 rounded-full " + STAGES[key].color} />
            {STAGES[key].label}
          </div>
        </SelectItem>
      );
    }
    return options;
  }

  return (
    <div data-testid="whiteboard-view">
      <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-4xl font-light tracking-tight text-foreground" style={{ fontFamily: 'var(--font-sans)' }}>
            The Whiteboard
          </h1>
          <p className="body-text mt-1">Sales pipeline at a glance</p>
        </div>
        <Button onClick={function() { setShowAddDeal(true); }} className="rounded-full" data-testid="add-deal-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Deal
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Total Pipeline" value={formatCurrency(totalPipeline)} />
        <StatCard icon={<Target className="w-4 h-4" />} label="Weighted Value" value={formatCurrency(weightedPipeline)} colorClass="text-green-600" />
        <StatCard icon={<Users className="w-4 h-4" />} label="Active Deals" value={deals.length} />
        <StatCard icon={<Target className="w-4 h-4" />} label="Avg Confidence" value={avgConfidence + "%"} />
      </div>

      <StageLegend />

      <Card className="border border-gray-300 overflow-hidden shadow-lg" data-testid="whiteboard-chart">
        <CardContent className="p-0">
          <div 
            className="relative w-full h-[500px] md:h-[600px]"
            style={{ background: '#F4F2EE' }}
          >
            {/* Whiteboard frame/border */}
            <div className="absolute inset-2 md:inset-4 border-4 border-gray-400 rounded-lg bg-white shadow-inner">
              {/* Grid lines like whiteboard faint lines */}
              {renderGridLines()}
              {/* Deal cards */}
              {renderDealCards()}
            </div>
            
            {/* Y-axis labels */}
            <div className="absolute left-6 md:left-8 top-10 bottom-20 flex flex-col justify-between text-xs md:text-sm text-gray-600 z-10">
              {renderYLabels()}
            </div>
            
            {/* X-axis labels */}
            <div className="absolute bottom-4 left-20 md:left-24 right-6 flex justify-between text-xs md:text-sm text-gray-600" style={{ fontFamily: 'var(--font-sans)' }}>
              <span>Today</span>
              <span>30 days</span>
              <span>60 days</span>
              <span>90 days</span>
              <span>120+ days</span>
            </div>
            
            {/* Empty state */}
            {deals.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <div className="text-6xl mb-4" style={{ fontFamily: 'var(--font-sans)', color: '#9E9B96' }}>📋</div>
                <p className="text-xl text-gray-500" style={{ fontFamily: 'var(--font-sans)' }}>Nothing on the board yet</p>
                <p className="text-sm text-gray-400 mb-4">Add your first deal to get started</p>
                <Button onClick={function() { setShowAddDeal(true); }} className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Deal
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between text-xs text-muted-foreground mt-2 px-4">
        <span>← Sooner to close</span>
        <span>Y-axis: Deal Value | X-axis: Days to Close</span>
        <span>Later to close →</span>
      </div>

      {/* Sales Leaderboard */}
      {leaderboard.length > 0 && (
        <Card className="border border-border mt-6" data-testid="leaderboard">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              <CardTitle className="text-base" style={{ fontFamily: 'var(--font-sans)' }}>Sales Leaderboard</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {leaderboard.map(function(rep, index) {
                const RankIcon = RANK_ICONS[index] || User;
                const rankColor = RANK_COLORS[index] || "text-muted-foreground";
                const maxWeighted = leaderboard[0] ? leaderboard[0].weightedValue : 1;
                const progressPercent = maxWeighted > 0 ? (rep.weightedValue / maxWeighted) * 100 : 0;
                
                return (
                  <div 
                    key={rep.name} 
                    className={"p-3 md:p-4 rounded-xl border transition-all " + (index === 0 ? "border-yellow-200 bg-yellow-50/50" : "border-border bg-secondary/20")}
                    data-testid={"leaderboard-" + index}
                  >
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className={"flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full " + (index === 0 ? "bg-yellow-100" : "bg-secondary")}>
                        <RankIcon className={"w-4 h-4 md:w-5 md:h-5 " + rankColor} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm md:text-base truncate">{rep.name}</span>
                            <Badge variant="outline" className="text-[10px] md:text-xs rounded-full">
                              {rep.dealCount} {rep.dealCount === 1 ? "deal" : "deals"}
                            </Badge>
                          </div>
                          <span className="text-sm md:text-base font-bold text-green-600" style={{ fontFamily: 'var(--font-sans)' }}>
                            {formatCurrency(rep.weightedValue)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Progress value={progressPercent} className="flex-1 h-2" />
                          <span className="text-[10px] md:text-xs text-muted-foreground whitespace-nowrap">
                            {formatCurrency(rep.totalValue)} total
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          {rep.stages.contract > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">{rep.stages.contract} contract</span>
                          )}
                          {rep.stages.verbal > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">{rep.stages.verbal} verbal</span>
                          )}
                          {rep.stages.negotiation > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">{rep.stages.negotiation} negotiation</span>
                          )}
                          {rep.stages.prospect > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{rep.stages.prospect} prospect</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selectedDeal} onOpenChange={function(open) { if (!open) setSelectedDeal(null); }}>
        <DialogContent className="sm:max-w-[500px]">
          {selectedDeal && (
            <div>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>{selectedDeal.deal_name}</DialogTitle>
                    <p className="text-sm text-muted-foreground mt-1">{selectedDeal.company_name}</p>
                  </div>
                  <Badge className={STAGES[selectedDeal.stage].color + " text-white rounded-full"}>
                    {STAGES[selectedDeal.stage].label}
                  </Badge>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Deal Value</p>
                    <p className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>{formatCurrency(selectedDeal.value)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Confidence</p>
                    <p className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>{selectedDeal.confidence}%</p>
                  </div>
                </div>

                {selectedDeal.sales_rep && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Sales Rep</p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm font-medium">{selectedDeal.sales_rep}</p>
                    </div>
                  </div>
                )}

                {selectedDeal.deal_owner_name && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Deal Owner</p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-500" />
                      <p className="text-sm font-medium">{selectedDeal.deal_owner_name}</p>
                    </div>
                  </div>
                )}

                {selectedDeal.submitted_by_name && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Submitted By</p>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-green-500" />
                      <p className="text-sm font-medium">{selectedDeal.submitted_by_name}</p>
                    </div>
                  </div>
                )}
                
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Expected Close</p>
                  <p className="text-sm font-medium">
                    {new Date(selectedDeal.estimated_close_date).toLocaleDateString('en-US', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                    <span className="text-muted-foreground ml-2">({selectedDeal.daysToClose} days)</span>
                  </p>
                </div>
                
                {selectedDeal.description && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{selectedDeal.description}</p>
                  </div>
                )}
                
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Added by {selectedDeal.created_by_name} on {new Date(selectedDeal.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={function() { handleDeleteDeal(selectedDeal.id); }}
                  className="rounded-full text-destructive hover:text-destructive"
                  data-testid="delete-deal-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button
                  variant="outline"
                  onClick={function() { startEdit(selectedDeal); setSelectedDeal(null); }}
                  className="rounded-full"
                  data-testid="edit-deal-btn"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={function() { setSelectedDeal(null); }} className="rounded-full">
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showAddDeal || !!editingDeal} onOpenChange={function(open) { 
        if (!open) { setShowAddDeal(false); setEditingDeal(null); resetForm(); }
      }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>
              {editingDeal ? "Edit Deal" : "Add New Deal"}
            </DialogTitle>
            <DialogDescription>
              {editingDeal ? "Update the deal details" : "Add a prospective deal to the whiteboard"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deal Name *</Label>
                <Input
                  value={newDeal.deal_name}
                  onChange={function(e) { setNewDeal(Object.assign({}, newDeal, { deal_name: e.target.value })); }}
                  placeholder="Website Redesign"
                  data-testid="input-deal-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company *</Label>
                <Input
                  value={newDeal.company_name}
                  onChange={function(e) { setNewDeal(Object.assign({}, newDeal, { company_name: e.target.value })); }}
                  placeholder="Acme Corp"
                  data-testid="input-company-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deal Value *</Label>
                <CurrencyInput
                  value={newDeal.value}
                  onChange={function(value) { setNewDeal(Object.assign({}, newDeal, { value: value || 0 })); }}
                  data-testid="input-deal-value"
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Close Date *</Label>
                <Input
                  type="date"
                  value={newDeal.estimated_close_date}
                  onChange={function(e) { setNewDeal(Object.assign({}, newDeal, { estimated_close_date: e.target.value })); }}
                  data-testid="input-close-date"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stage</Label>
                <Select
                  value={newDeal.stage}
                  onValueChange={function(value) { setNewDeal(Object.assign({}, newDeal, { stage: value })); }}
                >
                  <SelectTrigger data-testid="select-stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {renderStageOptions()}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Confidence: {newDeal.confidence}%</Label>
                <Slider
                  value={[newDeal.confidence]}
                  onValueChange={function(arr) { setNewDeal(Object.assign({}, newDeal, { confidence: arr[0] })); }}
                  min={0}
                  max={100}
                  step={5}
                  className="mt-2"
                  data-testid="slider-confidence"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Sales Rep</Label>
              <Input
                value={newDeal.sales_rep}
                onChange={function(e) { setNewDeal(Object.assign({}, newDeal, { sales_rep: e.target.value })); }}
                placeholder="John Smith"
                data-testid="input-sales-rep"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deal Owner</Label>
                <Select
                  value={newDeal.deal_owner_id}
                  onValueChange={function(value) { setNewDeal(Object.assign({}, newDeal, { deal_owner_id: value })); }}
                >
                  <SelectTrigger data-testid="select-deal-owner">
                    <SelectValue placeholder="Select owner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {users.map(function(u) {
                      return <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Submitted By</Label>
                <Select
                  value={newDeal.submitted_by_id}
                  onValueChange={function(value) { setNewDeal(Object.assign({}, newDeal, { submitted_by_id: value })); }}
                >
                  <SelectTrigger data-testid="select-submitted-by">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {users.map(function(u) {
                      return <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={newDeal.description}
                onChange={function(e) { setNewDeal(Object.assign({}, newDeal, { description: e.target.value })); }}
                placeholder="Any additional context about this deal..."
                rows={3}
                data-testid="input-description"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={function() { setShowAddDeal(false); setEditingDeal(null); resetForm(); }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={editingDeal ? handleUpdateDeal : handleAddDeal}
              className="rounded-full"
              data-testid="save-deal-btn"
            >
              {editingDeal ? "Save Changes" : "Add Deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhiteboardView;
